import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { REMOTE_DESIGN_CLASS } from '../../utils/remoteDesignTheme';
import { RemoteFlatIcon, type RemoteFlatIconKind } from './RemoteFlatIcons';
import type { RemoteAnswerKey, RemoteQuestionMode, RemoteQuestionType } from '../../types/presentationRemote';
import { QUESTION_TYPE_LABEL, answerKeyText, choiceAnswerText, choiceMultiAnswerText, judgeAnswer } from '../../utils/remoteQuestion';
import { communicationChoiceSwatchStyle } from '../../utils/presentationCommunicationAnswers';
import { BacklogIconCheck, useChromeInsets } from './RemoteMessageBacklog';
import './RemotePresentationOverlays.css';
import './RemoteDesignThemes.css';

export type RemoteNoticeView = { id: string; text: string; durationSec: number | null; shownAt: number };
export type RemoteQuestionView = {
  requestId: string;
  text: string;
  fromSessionId: string;
  submitted: boolean;
  /** 누락 시 단답형 */
  questionType?: RemoteQuestionType;
  choices?: string[];
  /** 누락 시 제출모드 */
  mode?: RemoteQuestionMode;
  answerKey?: RemoteAnswerKey;
  /** 선택형: 여러 개 골라 제출(정답이 2개 이상) */
  multiSelect?: boolean;
  /** 오답일 때 정답 표시 — 누락 시 true */
  revealAnswer?: boolean;
  /** 제출한 답(표시용) */
  submittedAnswer?: string;
};

const portal = (node: React.ReactNode) =>
  typeof document === 'undefined' ? null : createPortal(node, document.body);

/**
 * 공지·질문 공통 레이어: 현재 시각 영역 중심에 맞춰 최대 크기로 배치(없으면 뷰포트 중앙).
 * 카드 외 영역은 클릭 통과(기존 화면 조작 가능).
 */
const RemoteOverlayFrame: React.FC<{
  kind: 'notice' | 'question' | 'caption';
  title: string;
  closeLabel: string;
  onClose: () => void;
  role: 'alert' | 'dialog';
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** 상·하단 네비 사이 검정 반투명+블러 배경 (남은 메시지 줄이 이미 덮고 있으면 false) */
  backdrop?: boolean;
  /** 바깥(배경) 클릭 — 확인 없이 닫기 */
  onBackdropClose?: () => void;
}> = ({ kind, title, closeLabel, onClose, role, children, footer, backdrop = true, onBackdropClose }) => {
  const layerRef = useRef<HTMLDivElement | null>(null);
  const insets = useChromeInsets(layerRef, true);
  const layerH = layerRef.current?.clientHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 0);
  const style: React.CSSProperties = { top: insets.top + (layerH - insets.top - insets.bottom) / 2 };
  return portal(
    // 팝업 안의 키 입력은 페이지 단축키(슬라이드 넘김 등)로 전달하지 않음 (Escape는 닫기)
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={layerRef}
      className={`cp-remote-ov-layer ${REMOTE_DESIGN_CLASS}`}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape') onClose();
      }}
      onKeyUp={(e) => e.stopPropagation()}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={`cp-remote-ov-backdrop${backdrop ? '' : ' is-clear'}`}
        style={{ top: insets.top, bottom: insets.bottom }}
        onClick={onBackdropClose ?? onClose}
      />
      <div
        className={`cp-remote-ov-card cp-remote-ov-card--${kind === 'caption' ? 'notice cp-remote-ov-card--caption' : kind} is-centered is-compact`}
        style={style}
        role={role}
        aria-live={role === 'alert' ? 'assertive' : undefined}
        aria-label={title}
      >
        <header className="cp-remote-ov-head">
          <span className="cp-remote-ov-badge">
            {kind === 'caption' ? null : <RemoteFlatIcon kind={kind as RemoteFlatIconKind} tone="white" className="cp-remote-ov-badge-icon" />}
            {title}
          </span>
          <button type="button" className="cp-remote-ov-close" onClick={onClose} aria-label={closeLabel}>
            <img src="/modal-close.svg" alt="" width={20} height={20} />
          </button>
        </header>
        <div className="cp-remote-ov-body">{children}</div>
        {footer}
      </div>
    </div>
  );
};

export const RemoteNoticeOverlay: React.FC<{
  notice: RemoteNoticeView;
  onClose: () => void;
  /** 가운데 '확인' 버튼 — 확인 처리(남은 메시지에서 제거) */
  onConfirm?: () => void;
  backdrop?: boolean;
  onBackdropClose?: () => void;
  /** 확인한 공지 다시 보기 — 버튼 '닫기' */
  review?: boolean;
}> = ({ notice, onClose, onConfirm, backdrop, onBackdropClose, review = false }) => {
  const [progress, setProgress] = useState(1);
  useEffect(() => {
    if (!notice.durationSec) return undefined;
    const total = notice.durationSec * 1000;
    const id = window.setInterval(() => {
      setProgress(Math.max(0, 1 - (Date.now() - notice.shownAt) / total));
    }, 200);
    return () => window.clearInterval(id);
  }, [notice]);
  return (
    <RemoteOverlayFrame
      kind="notice"
      title="공지"
      closeLabel="공지 닫기"
      onClose={onClose}
      role="alert"
      backdrop={backdrop}
      onBackdropClose={onBackdropClose}
      footer={
        <>
          {onConfirm ? (
            <div className="cp-remote-ov-footer">
              <button type="button" className="cp-remote-ov-btn cp-remote-ov-btn--primary is-wide" onClick={onConfirm}>
                {review ? null : <BacklogIconCheck size={20} />}
                {review ? '닫기' : '확인'}
              </button>
            </div>
          ) : null}
          {notice.durationSec ? (
            <div className="cp-remote-ov-progress-track" aria-hidden>
              <div className="cp-remote-ov-progress" style={{ transform: `scaleX(${progress})` }} />
            </div>
          ) : null}
        </>
      }
    >
      <p className="cp-remote-ov-notice-text">{notice.text}</p>
    </RemoteOverlayFrame>
  );
};

export const RemoteQuestionOverlay: React.FC<{
  question: RemoteQuestionView;
  onSubmit: (content: string) => Promise<boolean>;
  onAnswerAgain: () => void;
  /** 다시 답하기 버튼 문구 — 기본 ‘추가 답변 제출’(메인/수업), 소통은 ‘수정 답변 제출’ */
  answerAgainLabel?: string;
  onDismiss: () => void;
  /** 남은 메시지 줄에서 연 경우 — 가운데 '제출하기' */
  fromBacklog?: boolean;
  backdrop?: boolean;
  onBackdropClose?: () => void;
  /** 확인/제출한 질문 다시 보기 — 다시 제출 불가 */
  readOnly?: boolean;
  /** 제목 뒤 출처 표시(예: '질문 페이지') — 없으면 생략 */
  sourceLabel?: string;
}> = ({ question, onSubmit, onAnswerAgain, answerAgainLabel = '추가 답변 제출', onDismiss, fromBacklog = false, backdrop, onBackdropClose, readOnly = false, sourceLabel }) => {
  const qType = question.questionType ?? 'short';
  const choices = question.choices ?? [];
  const [value, setValue] = useState('');
  const [picked, setPicked] = useState<number | null>(null);
  /** 선택형 복수 선택 */
  const [multiPicked, setMultiPicked] = useState<number[]>([]);
  const isMulti = qType === 'choice' && !!question.multiSelect;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (!question.submitted) {
      setValue('');
      setPicked(null);
      setMultiPicked([]);
    }
  }, [question.requestId, question.submitted]);
  // 단답 입력: 내용에 맞춰 높이 자동 → 필드 안에서 세로 가운데
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (qType === 'essay') {
      el.style.height = ''; // 서술형: 고정 높이 + 스크롤
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, qType, question.submitted]);

  const answerContent = (): string => {
    if (qType === 'short' || qType === 'essay') return value.trim();
    if (isMulti) return choiceMultiAnswerText(multiPicked, choices);
    if (picked == null) return '';
    if (qType === 'ox') return picked === 0 ? 'O' : 'X';
    const c = choices[picked];
    return c ? choiceAnswerText(picked, c) : '';
  };
  const content = answerContent();
  const isAnswerMode = question.mode === 'answer' && !!question.answerKey;
  const verdict =
    isAnswerMode && question.submittedAnswer
      ? judgeAnswer(qType, question.answerKey, question.submittedAnswer, choices)
      : null;
  const keyText = isAnswerMode ? answerKeyText(qType, question.answerKey, choices) : '';
  const submit = async () => {
    if (!content || busy) return;
    setBusy(true);
    setError('');
    const ok = await onSubmit(content);
    setBusy(false);
    if (!ok) setError('전송하지 못했어요. 다시 시도해 주세요.');
  };

  const focusInput = (e: React.MouseEvent) => {
    if (e.target === inputRef.current) return;
    e.preventDefault(); // 포커스가 래퍼로 가지 않게
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const n = el.value.length;
    el.setSelectionRange(n, n);
  };

  let field: React.ReactNode;
  if (qType === 'ox') {
    field = (
      <div className="cp-remote-ov-ox" role="radiogroup" aria-label="O 또는 X 선택">
        {(['O', 'X'] as const).map((k, i) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={picked === i}
            aria-label={k === 'O' ? '맞아요 (O)' : '아니에요 (X)'}
            className={`cp-remote-ov-ox-btn cp-remote-ov-ox-btn--${k === 'O' ? 'o' : 'x'}${picked === i ? ' is-on' : ''}`}
            onClick={() => setPicked(i)}
          >
            <OxMark kind={k} />
          </button>
        ))}
      </div>
    );
  } else if (qType === 'choice') {
    field = (
      <div
        className="cp-remote-ov-choices is-page-style"
        role={isMulti ? 'group' : 'radiogroup'}
        aria-label={isMulti ? '보기 선택 (정답을 모두 고르세요)' : '보기 선택'}
      >
        {choices.map((c, i) => {
          const on = isMulti ? multiPicked.includes(i) : picked === i;
          return (
          <button
            key={i}
            type="button"
            role={isMulti ? 'checkbox' : 'radio'}
            aria-checked={on}
            className={`cp-remote-ov-choice${on ? ' is-on' : ''}`}
            onClick={() =>
              isMulti
                ? setMultiPicked((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]))
                : setPicked(i)
            }
          >
            <span className="cp-remote-ov-choice-num cp-choice-swatch" style={communicationChoiceSwatchStyle(i)}>
              {i + 1}번
            </span>
            <span className="cp-remote-ov-choice-text">{c}</span>
          </button>
          );
        })}
      </div>
    );
  } else {
    field = (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
      <div className={`cp-remote-ov-answer-field${qType === 'essay' ? ' is-essay' : ''}`} onMouseDown={focusInput}>
        <textarea
          ref={inputRef}
          className="cp-remote-ov-answer"
          rows={qType === 'essay' ? 4 : 1}
          value={value}
          maxLength={500}
          aria-label="답 입력"
          placeholder={qType === 'essay' ? '여기에 생각을 자세히 써 주세요' : '여기에 답을 입력하세요'}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.nativeEvent.isComposing) return;
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit();
          }}
        />
      </div>
    );
  }

  return (
    <RemoteOverlayFrame
      kind="question"
      title={`질문${sourceLabel ? ` · ${sourceLabel}` : ''}${qType === 'short' ? '' : ` · ${QUESTION_TYPE_LABEL[qType]}`}`}
      closeLabel="질문 창 숨기기"
      onClose={onDismiss}
      role="dialog"
      backdrop={backdrop}
      onBackdropClose={onBackdropClose}
      footer={readOnly ? (
        <div className="cp-remote-ov-footer">
          <button type="button" className="cp-remote-ov-btn cp-remote-ov-btn--primary is-wide" onClick={onDismiss}>
            닫기
          </button>
        </div>
      ) : question.submitted ? undefined : (
          <div className="cp-remote-ov-footer cp-remote-ov-actions is-centered">
            {error ? (
              <p role="alert" className="cp-remote-ov-error">
                {error}
              </p>
            ) : null}
            <button
              type="button"
              className={`cp-remote-ov-btn cp-remote-ov-btn--primary is-wide`}
              disabled={busy || !content}
              onClick={() => void submit()}
            >
              {fromBacklog && !busy ? <BacklogIconCheck size={20} /> : null}
              {busy ? '전송 중…' : fromBacklog ? '제출하기' : '제출'}
            </button>
          </div>
      )}
    >
      <p className="cp-remote-ov-question-text">{question.text}</p>
      {isMulti && !question.submitted && !readOnly ? <p className="cp-remote-ov-multi-hint">정답을 모두 고르세요</p> : null}
      {readOnly && !question.submittedAnswer ? (
        <div className="cp-remote-ov-done-row">
          <p className="cp-remote-ov-keyline">답변하지 않은 질문이에요</p>
        </div>
      ) : question.submitted || readOnly ? (
        <div className="cp-remote-ov-done-row">
          {verdict == null ? (
            <p className="cp-remote-ov-done">✓ 제출 완료</p>
          ) : (
            <p className={`cp-remote-ov-verdict ${verdict ? 'is-correct' : 'is-wrong'}`} role="status">
              <OxMark kind={verdict ? 'O' : 'X'} small />
              {verdict ? '정답이에요!' : '오답이에요'}
            </p>
          )}
          {question.submittedAnswer ? (
            <p className="cp-remote-ov-done-answer">
              {qType === 'ox' && (question.submittedAnswer === 'O' || question.submittedAnswer === 'X') ? (
                <OxMark kind={question.submittedAnswer} small />
              ) : (
                question.submittedAnswer
              )}
            </p>
          ) : null}
          {verdict === false && keyText && question.revealAnswer !== false ? (
            <p className="cp-remote-ov-keyline">
              정답: <strong>{keyText}</strong>
            </p>
          ) : null}
          {readOnly ? null : (
            <button type="button" className="cp-remote-ov-btn" onClick={onAnswerAgain}>
              {answerAgainLabel}
            </button>
          )}
        </div>
      ) : (
        <div className={`cp-remote-ov-answer-wrap cp-remote-ov-answer-wrap--${qType}`}>
          {field}
        </div>
      )}
    </RemoteOverlayFrame>
  );
};

/** 평면 O / X 기호 */
export const OxMark: React.FC<{ kind: 'O' | 'X'; small?: boolean }> = ({ kind, small }) => (
  <svg
    className={`cp-remote-ov-oxmark cp-remote-ov-oxmark--${kind === 'O' ? 'o' : 'x'}${small ? ' is-small' : ''}`}
    viewBox="0 0 100 100"
    aria-hidden
  >
    {kind === 'O' ? (
      <circle cx="50" cy="50" r="33" fill="none" stroke="currentColor" strokeWidth="14" />
    ) : (
      <g stroke="currentColor" strokeWidth="14" strokeLinecap="round">
        <line x1="22" y1="22" x2="78" y2="78" />
        <line x1="78" y1="22" x2="22" y2="78" />
      </g>
    )}
  </svg>
);

/** 자동재생 차단 시 첫 터치로 재생 */
export const RemoteBgmGestureOverlay: React.FC<{ onTap: () => void; message?: string }> = ({ onTap, message }) => {
  const firedRef = React.useRef(false);
  const fire = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onTap();
    window.setTimeout(() => {
      firedRef.current = false;
    }, 400);
  };
  return portal(
    <button type="button" className="cp-remote-ov-gesture" onPointerDown={fire} onClick={fire} aria-label="재생 허용">
      <span className="cp-remote-ov-gesture-pill">
        <RemoteFlatIcon kind="bgm" /> {message ?? '화면을 한 번 터치하면 음악이 재생돼요'}</span>
    </button>
  );
};

/** 자막(누르고 말하기): 공지 팝업과 같은 크기·위치의 그린 블록. 자동으로 사라지지 않음 */
export const RemoteCaptionOverlay: React.FC<{ text: string; onClose: () => void; onBackdropClose?: () => void }> = ({
  text,
  onClose,
  onBackdropClose,
}) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLParagraphElement | null>(null);
  /* 넘치면 글자 크기를 최소까지 줄이고, 그래도 넘치면 최신 줄이 보이도록 아래로 스크롤 */
  useEffect(() => {
    const box = boxRef.current;
    const p = textRef.current;
    if (!box || !p) return;
    p.style.fontSize = '';
    const max = parseFloat(getComputedStyle(p).fontSize) || 64;
    const min = 24;
    let size = max;
    while (size > min && box.scrollHeight > box.clientHeight + 1) {
      size = Math.max(min, size - Math.max(2, size * 0.08));
      p.style.fontSize = `${size}px`;
    }
    box.scrollTop = box.scrollHeight;
  }, [text]);
  return (
    <RemoteOverlayFrame
      kind="caption"
      title="자막"
      closeLabel="자막 닫기"
      onClose={onClose}
      role="dialog"
      backdrop={false}
      onBackdropClose={onBackdropClose ?? onClose}
    >
      <div ref={boxRef} className="cp-remote-ov-caption-box" aria-live="polite">
        <p ref={textRef} className="cp-remote-ov-notice-text cp-remote-ov-caption-text">
          {text}
        </p>
      </div>
    </RemoteOverlayFrame>
  );
};
